import { Fragment, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ImagePlus,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Textarea, cn } from '@mlm/ui';
import { apiErrorMessage, listCompPlanConfigs, rankIconUrl, uploadRankIcon } from '../../lib/api';

/** Mirrors the API's rank-icon checks (IMAGE_TYPES, MAX_RANK_ICON_BYTES), so a bad file fails before the upload. */
const BADGE_TYPES = new Set(['image/png', 'image/jpeg']);
const BADGE_MAX_BYTES = 2 * 1024 * 1024;

type BadgeStatus = { uploading?: boolean; error?: string };

/** Requirement keys the member rank tracker can hide per rank. */
export type RankRequirementKey = 'ppv' | 'gpv' | 'legs' | 'directs' | 'total_earned';

const TRACKER_KEYS: { key: RankRequirementKey; label: string }[] = [
  { key: 'ppv', label: 'PPV' },
  { key: 'gpv', label: 'GPV' },
  { key: 'legs', label: 'Legs' },
  { key: 'directs', label: 'Directs' },
  { key: 'total_earned', label: 'Total Earned' },
];

export type StairstepRank = {
  code: string;
  min_ppv: number;
  min_gpv: number;
  min_legs: number;
  /** Min directly-sponsored active slots required for this rank (Phase C). */
  min_directs?: number;
  min_leg_gpv: number | null;
  min_leg_rank: string | null;
  payout_pct: number;
  discount_pct: number;
  /**
   * Same-rank royalty bonus %. When > 0, an equal-rank downline that breaks away
   * pays this rank a royalty on the leg's whole group PV (instead of 0). Set it
   * only on the specific ranks that should earn royalty; blank/0 = off.
   */
  royalty_pct?: number;
  /** Infinity Bonus override % for this rank, read by the infinity_bonus plan (Phase C). */
  percentage_override?: number;
  /** One-time rank-up bonus (paid when the tenant flag stairstep_rankup_bonus is on). */
  reward_cash?: number;
  reward_gc?: number;
  /** Min cumulative cash Total Earned of the slot across all plans (0 = none). */
  min_total_earned?: number;
  /** Display-only fields (never read by the comp math). */
  name?: string;
  description?: string;
  /** R2 key from `uploadRankIcon`, served publicly at `/ranks/<code>/icon`. */
  icon_key?: string;
  /** Requirements hidden from the member rank tracker. */
  tracker_hidden?: RankRequirementKey[];
};

export type StairstepConfig = {
  ranks: StairstepRank[];
};

export function StairstepEditor({
  value,
  onChange,
  readOnly,
}: {
  value: StairstepConfig;
  onChange: (next: StairstepConfig) => void;
  readOnly?: boolean;
}) {
  // Index of the rank whose Details row is expanded (follows the rank on reorder).
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  // Object URLs of badges uploaded this session, by icon key. The public
  // `/ranks/<code>/icon` route only serves a PUBLISHED key, so a draft badge
  // previews from here until it's published.
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const previewUrls = useRef<string[]>([]);
  // Latest PUBLISHED config (same cache entry as the Comp Plan page), to flag
  // badges that aren't live yet.
  const configsQ = useQuery({ queryKey: ['admin-compplan-configs'], queryFn: listCompPlanConfigs });
  const published = (configsQ.data ?? [])
    .filter((c) => c.planCode === 'stairstep')
    .sort((a, b) => b.version - a.version)[0]?.config as StairstepConfig | undefined;
  // Badge upload state by row index; cleared on reorder/remove.
  const [badgeStatus, setBadgeStatus] = useState<Record<number, BadgeStatus>>({});
  // An upload resolves after the admin may have edited other fields, so its
  // patch must apply to the latest draft, not the one it started from.
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => () => previewUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  // A badge dropped just outside its target would make the browser open the
  // image and throw away the unpublished draft.
  useEffect(() => {
    if (readOnly) return;
    const guard = (e: globalThis.DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => {
      window.removeEventListener('dragover', guard);
      window.removeEventListener('drop', guard);
    };
  }, [readOnly]);

  function setRank(i: number, patch: Partial<StairstepRank>) {
    const next = valueRef.current.ranks.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ ranks: next });
  }
  function removeRank(i: number) {
    onChange({ ranks: value.ranks.filter((_, idx) => idx !== i) });
    setOpenIdx((o) => (o === null || o === i ? null : o > i ? o - 1 : o));
    setBadgeStatus({});
  }
  /** Swap rank i with its neighbour (dir -1 = up = lower rank, +1 = down = higher). */
  function moveRank(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.ranks.length) return;
    const next = value.ranks.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange({ ranks: next });
    setOpenIdx((o) => (o === i ? j : o === j ? i : o));
    setBadgeStatus({});
  }

  async function uploadBadge(i: number, file: File) {
    const invalid = !BADGE_TYPES.has(file.type)
      ? 'Badges must be PNG or JPEG images.'
      : file.size > BADGE_MAX_BYTES
        ? `That image is ${(file.size / 1024 / 1024).toFixed(1)} MB. Badges can be up to 2 MB.`
        : null;
    if (invalid) {
      setBadgeStatus((s) => ({ ...s, [i]: { error: invalid } }));
      setOpenIdx(i);
      return;
    }
    setBadgeStatus((s) => ({ ...s, [i]: { uploading: true } }));
    try {
      const { key } = await uploadRankIcon(file);
      const url = URL.createObjectURL(file);
      previewUrls.current.push(url);
      setPreviews((p) => ({ ...p, [key]: url }));
      setRank(i, { icon_key: key });
      setBadgeStatus((s) => ({ ...s, [i]: {} }));
    } catch (err) {
      setBadgeStatus((s) => ({
        ...s,
        [i]: { error: apiErrorMessage(err, 'The badge didn’t upload. Try again.') },
      }));
      setOpenIdx(i);
    }
  }

  function badgeSrc(r: StairstepRank): string | null {
    if (!r.icon_key) return null;
    // `?v=` busts the browser cache when a published badge is replaced.
    return previews[r.icon_key] ?? `${rankIconUrl(r.code)}?v=${encodeURIComponent(r.icon_key)}`;
  }

  function badgeNotLive(r: StairstepRank): boolean {
    if (!published) return false;
    const live = published.ranks?.find((p) => p.code === r.code)?.icon_key ?? '';
    return (r.icon_key ?? '') !== live;
  }
  function addRank() {
    onChange({
      ranks: [
        ...value.ranks,
        {
          code: `rank_${value.ranks.length + 1}`,
          min_ppv: 0,
          min_gpv: 0,
          min_legs: 0,
          min_directs: 0,
          min_total_earned: 0,
          min_leg_gpv: null,
          min_leg_rank: null,
          payout_pct: 0,
          discount_pct: 0,
          royalty_pct: 0,
          percentage_override: 0,
          reward_cash: 0,
          reward_gc: 0,
        },
      ],
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Ranks</h3>
          <p className="text-[10px] text-muted-foreground">
            Hierarchy = list order, lowest → highest:{' '}
            <strong>top row is the lowest rank, bottom row the highest</strong> (use the ↑/↓ arrows
            to reorder). A slot earns the highest rank whose PPV / GPV / leg thresholds it meets.
            Hover a column header for what it does. Click a rank&apos;s Badge square, or drop an
            image on it, to upload its badge.
          </p>
        </div>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={addRank}>
            <Plus className="h-3 w-3 mr-1" />
            Add rank
          </Button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border rounded-md min-w-[900px]">
          <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
            <tr>
              <th
                className="px-3 py-2 font-medium"
                title="Rank order, lowest → highest. Use the arrows to reorder."
              >
                Rank #
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Badge members see on their dashboard and the rank showcase. Click or drop a PNG/JPEG to upload."
              >
                Badge
              </th>
              <th className="px-3 py-2 font-medium">Code</th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum personal point value (own orders) for this rank."
              >
                Min PPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum group point value (own + all descendants) for this rank."
              >
                Min GPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum number of qualifying direct legs required for this rank."
              >
                Min legs
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Minimum directly-sponsored ACTIVE slots required, in addition to qualifying legs."
              >
                Min directs
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Cumulative Total Earned of the slot across all comp plans (cash). 0 = no requirement."
              >
                Min Total Earned
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="A leg counts toward Min legs if its group PV is at least this (when Min leg rank is blank)."
              >
                Min leg GPV
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="A leg counts toward Min legs only if its OWN rank is at least this rank code."
              >
                Min leg rank
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="This rank's differential commission %. An upline earns (uplinePct − downlinePct) on a downline's PPV."
              >
                Payout %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Same-rank royalty %. When set, an equal-rank downline that breaks away still pays this rank a royalty on the leg's whole group PV (instead of 0). Leave 0 to disable."
              >
                Royalty %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="Infinity Bonus override % — read by the separate Infinity Bonus plan, NOT by Stairstep itself."
              >
                Infinity %
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="One-time cash bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on)."
              >
                Reward cash
              </th>
              <th
                className="px-3 py-2 font-medium"
                title="One-time gift-cert bonus paid when a slot first reaches this rank (only when the tenant's rank-up bonus setting is on)."
              >
                Reward GC
              </th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {value.ranks.length === 0 ? (
              <tr>
                <td colSpan={COLS} className="px-3 py-4 text-xs text-muted-foreground text-center">
                  No ranks configured. Click "Add rank" to start.
                </td>
              </tr>
            ) : (
              value.ranks.map((r, i) => (
                <Fragment key={i}>
                  <tr className="border-t">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <span className="w-5 text-xs tabular-nums text-muted-foreground">
                          {i + 1}
                        </span>
                        {!readOnly ? (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveRank(i, -1)}
                              disabled={i === 0}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              aria-label="Move rank up (lower)"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveRank(i, 1)}
                              disabled={i === value.ranks.length - 1}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              aria-label="Move rank down (higher)"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <RankBadgeButton
                        rankLabel={r.name?.trim() || r.code}
                        src={badgeSrc(r)}
                        status={badgeStatus[i]}
                        notLive={badgeNotLive(r)}
                        readOnly={readOnly}
                        onFile={(f) => void uploadBadge(i, f)}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                          value={r.code}
                          disabled={readOnly}
                          onChange={(e) => setRank(i, { code: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setOpenIdx(openIdx === i ? null : i)}
                          className="inline-flex h-8 items-center gap-0.5 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-expanded={openIdx === i}
                          aria-label={`Details for rank ${r.code}`}
                          title="Badge, display name, description, tracker visibility"
                        >
                          {openIdx === i ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          Details
                        </button>
                      </div>
                    </td>
                    {(
                      ['min_ppv', 'min_gpv', 'min_legs', 'min_directs', 'min_total_earned'] as const
                    ).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={key === 'min_legs' || key === 'min_directs' ? 1 : 0.01}
                          min={0}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                        placeholder=""
                        value={r.min_leg_gpv ?? ''}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setRank(i, {
                            min_leg_gpv: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      {/* Must be an actual rank CODE — a free-text typo (e.g. a
                        rank number) silently disables the leg-rank gate, so
                        constrain to configured codes. */}
                      <select
                        className="h-8 w-28 px-2 rounded-md border bg-background text-xs font-mono"
                        value={r.min_leg_rank ?? ''}
                        disabled={readOnly}
                        onChange={(e) =>
                          setRank(i, {
                            min_leg_rank: e.target.value === '' ? null : e.target.value,
                          })
                        }
                      >
                        <option value="">none</option>
                        {value.ranks
                          .filter((rk) => rk.code !== r.code)
                          .map((rk) => (
                            <option key={rk.code} value={rk.code}>
                              {rk.code}
                            </option>
                          ))}
                      </select>
                    </td>
                    {(['payout_pct', 'royalty_pct', 'percentage_override'] as const).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-20 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          max={100}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    {(['reward_cash', 'reward_gc'] as const).map((key) => (
                      <td className="px-3 py-1.5" key={key}>
                        <input
                          type="number"
                          className="h-8 w-24 px-2 rounded-md border bg-background text-xs"
                          value={r[key] ?? 0}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          onChange={(e) =>
                            setRank(i, {
                              [key]: Number(e.target.value) || 0,
                            } as Partial<StairstepRank>)
                          }
                        />
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      {!readOnly ? (
                        <button
                          type="button"
                          onClick={() => removeRank(i)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          aria-label={`Remove rank ${r.code}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {openIdx === i ? (
                    <tr className="bg-muted/20">
                      {/* Sticky so the panel stays in view while the wide
                        table is scrolled sideways. */}
                      <td colSpan={COLS} className="p-0">
                        <div className="sticky left-0 w-fit px-3 py-3">
                          <RankDetails
                            rank={r}
                            readOnly={readOnly}
                            onPatch={(patch) => setRank(i, patch)}
                            badge={
                              <RankBadgeField
                                rankLabel={r.name?.trim() || r.code}
                                src={badgeSrc(r)}
                                hasBadge={!!r.icon_key}
                                status={badgeStatus[i]}
                                notLive={badgeNotLive(r)}
                                readOnly={readOnly}
                                onFile={(f) => void uploadBadge(i, f)}
                                onRemove={() => {
                                  setRank(i, { icon_key: undefined });
                                  setBadgeStatus((s) => ({ ...s, [i]: {} }));
                                }}
                              />
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Table column count (the Details row spans all of them). */
const COLS = 16;

/**
 * Click-to-pick plus drag-and-drop for one badge target. The hidden input is
 * returned for the caller to render; `over` drives the drop-target styling.
 */
function useBadgePicker(onFile: (f: File) => void, disabled?: boolean) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const dropProps = disabled
    ? {}
    : {
        onDragOver: (e: DragEvent) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        },
      };
  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/png,image/jpeg"
      className="hidden"
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) onFile(f);
        e.target.value = '';
      }}
    />
  );
  return { over, open: () => inputRef.current?.click(), dropProps, input };
}

/** Badge preview that falls back to a placeholder when the image can't load. */
function BadgeImage({ src, className }: { src: string | null; className: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) {
    return <ImagePlus className="h-4 w-4 text-muted-foreground" aria-hidden />;
  }
  return <img src={src} alt="" className={className} onError={() => setFailed(src)} />;
}

/** The rank row's badge square: shows the badge, uploads on click or drop. */
function RankBadgeButton({
  rankLabel,
  src,
  status,
  notLive,
  readOnly,
  onFile,
}: {
  rankLabel: string;
  src: string | null;
  status?: BadgeStatus;
  notLive: boolean;
  readOnly?: boolean;
  onFile: (f: File) => void;
}) {
  const uploading = !!status?.uploading;
  const picker = useBadgePicker(onFile, readOnly || uploading);
  const box = 'relative grid h-10 w-10 shrink-0 place-items-center rounded-md border bg-white';
  const image = <BadgeImage src={src} className="h-full w-full rounded-md object-contain p-1" />;

  if (readOnly) return <div className={box}>{image}</div>;
  return (
    <>
      <button
        type="button"
        onClick={picker.open}
        disabled={uploading}
        {...picker.dropProps}
        className={cn(
          box,
          'transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          !src && 'border-dashed',
          picker.over && 'border-primary ring-2 ring-primary/30',
          status?.error && 'border-destructive',
        )}
        aria-label={`${src ? 'Replace' : 'Upload'} badge for ${rankLabel}`}
        title={
          status?.error ??
          (notLive
            ? 'Not live yet: publish a new version to show this badge to members.'
            : src
              ? 'Click or drop an image to replace the badge'
              : 'Click or drop a PNG/JPEG to upload a badge')
        }
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : image}
        {notLive && !uploading ? (
          <span
            className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-warning ring-2 ring-background"
            aria-hidden
          />
        ) : null}
      </button>
      {picker.input}
    </>
  );
}

/** The Details panel's badge uploader: large preview, drop zone, replace / remove. */
function RankBadgeField({
  rankLabel,
  src,
  hasBadge,
  status,
  notLive,
  readOnly,
  onFile,
  onRemove,
}: {
  rankLabel: string;
  src: string | null;
  hasBadge: boolean;
  status?: BadgeStatus;
  notLive: boolean;
  readOnly?: boolean;
  onFile: (f: File) => void;
  onRemove: () => void;
}) {
  const uploading = !!status?.uploading;
  const picker = useBadgePicker(onFile, readOnly || uploading);

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium">Badge</span>
      <div
        {...picker.dropProps}
        className={cn(
          'flex items-center gap-3 rounded-md border bg-background p-3 transition-colors',
          !readOnly && 'border-dashed',
          picker.over && 'border-primary bg-primary/5',
        )}
      >
        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-md border bg-white">
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <BadgeImage src={src} className="h-full w-full rounded-md object-contain p-1.5" />
          )}
        </div>
        {readOnly ? (
          <p className="text-xs text-muted-foreground">{hasBadge ? rankLabel : 'No badge'}</p>
        ) : (
          <div className="min-w-0 space-y-2">
            <p className="text-xs text-muted-foreground">
              {picker.over
                ? 'Release to upload'
                : uploading
                  ? 'Uploading…'
                  : 'Drop a PNG or JPEG here, up to 2 MB. Square images look best.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={picker.open}
                disabled={uploading}
              >
                {hasBadge ? 'Replace badge' : 'Choose image'}
              </Button>
              {hasBadge ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={onRemove}
                  disabled={uploading}
                >
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
        )}
        {picker.input}
      </div>
      {status?.error ? (
        <p className="text-[11px] text-destructive" role="alert">
          {status.error}
        </p>
      ) : notLive ? (
        <p className="text-[11px] text-warning">
          Not live yet. Publish a new version below to show this change to members.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Members see it on their dashboard and in the rank showcase.
        </p>
      )}
    </div>
  );
}

/**
 * Expanded per-rank details: badge, display name, description and which
 * requirements the member tracker shows. Saved with the config on publish.
 */
function RankDetails({
  rank,
  readOnly,
  onPatch,
  badge,
}: {
  rank: StairstepRank;
  readOnly?: boolean;
  onPatch: (patch: Partial<StairstepRank>) => void;
  badge: ReactNode;
}) {
  const hidden = new Set(rank.tracker_hidden ?? []);

  function toggleTracker(key: RankRequirementKey, shown: boolean) {
    const next = new Set(hidden);
    if (shown) next.delete(key);
    else next.add(key);
    onPatch({ tracker_hidden: TRACKER_KEYS.map((t) => t.key).filter((k) => next.has(k)) });
  }

  return (
    <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
      {badge}
      <label className="space-y-1">
        <span className="text-xs font-medium">Display name</span>
        <Input
          className="h-8 text-xs"
          placeholder={rank.code}
          value={rank.name ?? ''}
          disabled={readOnly}
          onChange={(e) => onPatch({ name: e.target.value || undefined })}
        />
        <span className="block text-[11px] text-muted-foreground">
          Shown to members and on the storefront. Blank = the rank code.
        </span>
      </label>
      <label className="space-y-1 sm:col-span-2">
        <span className="text-xs font-medium">Description</span>
        <Textarea
          rows={3}
          className="text-xs"
          value={rank.description ?? ''}
          disabled={readOnly}
          onChange={(e) => onPatch({ description: e.target.value || undefined })}
        />
      </label>
      <div className="space-y-1 sm:col-span-2">
        <span className="text-xs font-medium">Show on member tracker</span>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {TRACKER_KEYS.map((t) => (
            <label key={t.key} className="flex items-center gap-1.5 text-xs">
              <Checkbox
                checked={!hidden.has(t.key)}
                disabled={readOnly}
                onCheckedChange={(v) => toggleTracker(t.key, !!v)}
              />
              {t.label}
            </label>
          ))}
        </div>
        <span className="block text-[11px] text-muted-foreground">
          Requirements set to 0 are always hidden.
        </span>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- the empty-config factory stays beside its editor, like every sibling plan editor
export function emptyStairstepConfig(): StairstepConfig {
  return { ranks: [] };
}
